import { getUserSetting, saveUserSetting } from "@src/utils/storage.utils";

const parseStoredArray = (stored: string): Array<string> | null => {
	if (!stored) return null;
	try {
		const parsed = JSON.parse(stored);
		if (!Array.isArray(parsed)) return null;
		return parsed.filter((x): x is string => typeof x === "string");
	} catch {
		return null;
	}
};

export const readStoredColumnList = (key: string, defaults: Array<string>): Array<string> =>
	parseStoredArray(getUserSetting(key, "")) ?? defaults;

export const readStoredColumnOrder = (key: string, defaults: Array<string>): Array<string> => {
	const stored = parseStoredArray(getUserSetting(key, ""));
	if (!stored) return defaults;

	const known = new Set(defaults);
	const order = stored.filter((id) => known.has(id));

	defaults.forEach((id, index) => {
		if (order.includes(id)) return;
		const previous = defaults.slice(0, index).reverse().find((candidate) => order.includes(candidate));
		order.splice(previous ? order.indexOf(previous) + 1 : 0, 0, id);
	});

	return order;
};

export const writeStoredColumnList = (key: string, value: Array<string>) => saveUserSetting(key, JSON.stringify(value));

const widthsKey = (tableId: string) => `gridColumnWidths:${tableId}`;

export const readStoredColumnWidths = (tableId: string): Record<string, number> => {
	if (!tableId) return {};

	try {
		const parsed = JSON.parse(getUserSetting(widthsKey(tableId), "") || "{}");
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};

		return Object.entries(parsed).reduce<Record<string, number>>((acc, [columnId, width]) => {
			if (typeof width === "number" && Number.isFinite(width) && width > 0) acc[columnId] = width;
			return acc;
		}, {});
	} catch {
		return {};
	}
};

export const writeStoredColumnWidth = (tableId: string, columnId: string, width: number) => {
	if (!tableId || !columnId) return;

	const widths = { ...readStoredColumnWidths(tableId), [columnId]: width };
	saveUserSetting(widthsKey(tableId), JSON.stringify(widths));
};

export const GRID_WIDTHS_RESET_EVENT = "grid:widths-reset";

export const clearStoredColumnWidths = (tableIds: Array<string>) => {
	tableIds.filter(Boolean).forEach((tableId) => {
		saveUserSetting(widthsKey(tableId), null);
		window.dispatchEvent(new CustomEvent(GRID_WIDTHS_RESET_EVENT, { detail: { tableId } }));
	});
};

export const GRID_TABLE_IDS: Record<string, Array<string>> = {
	Event: ["events"],
	Inventory: ["inventory", "global-inventory"],
	UngroupedInventory: ["inventory"],
};

export const writeStoredColumnWidths = (tableId: string, widths: Record<string, number>) => {
	if (!tableId) return;

	const sanitized = Object.entries(widths).reduce<Record<string, number>>((acc, [columnId, width]) => {
		if (typeof width === "number" && Number.isFinite(width) && width > 0) acc[columnId] = width;
		return acc;
	}, {});

	saveUserSetting(widthsKey(tableId), JSON.stringify(sanitized));
};

export const GRID_TOGGLE_PARAMS: Array<string> = [
	"showSold",
	"showOnHold",
	"showParking",
	"showCanceled",
	"unshared",
	"showUntaggedInventory",
];

export const TOGGLE_ABSENT = "__absent__";

export const INVENTORY_TOGGLE_KEYS = {
	showSold: "inventoryShowSold",
	showOnHold: "inventoryShowOnHold",
	groupInventory: "inventoryGroupInventory",
} as const;

export const GLOBAL_INVENTORY_TOGGLE_KEYS = {
	showSold: "globalInventoryShowSold",
	showOnHold: "globalInventoryShowOnHold",
} as const;

export const readStoredBooleanOrNull = (key: string): boolean | null => {
	const stored = getUserSetting(key, "");
	if (stored !== "true" && stored !== "false") return null;
	return stored === "true";
};

export const readStoredBoolean = (key: string, defaultValue: boolean): boolean => readStoredBooleanOrNull(key) ?? defaultValue;

export const writeStoredBoolean = (key: string, value: boolean) => saveUserSetting(key, value);

const TOGGLES_KEY = "gridToggleStates";

export const readStoredToggles = (): Record<string, string> => {
	try {
		const parsed = JSON.parse(getUserSetting(TOGGLES_KEY, "") || "{}");
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};

		return Object.entries(parsed).reduce<Record<string, string>>((acc, [key, value]) => {
			if (GRID_TOGGLE_PARAMS.includes(key) && typeof value === "string") acc[key] = value;
			return acc;
		}, {});
	} catch {
		return {};
	}
};

export const writeStoredToggle = (key: string, value: string) => {
	if (!GRID_TOGGLE_PARAMS.includes(key)) return;

	const toggles = { ...readStoredToggles(), [key]: value };
	saveUserSetting(TOGGLES_KEY, JSON.stringify(toggles));
};
